import React, { useEffect, useState } from "react";
import {
  fetchUniversities,
  createUniversity,
  updateUniversity,
  deleteUniversity,
} from "../APIs/universityApi";

const ROWS_PER_PAGE = 100;

function UniversityTab() {
  const [universities, setUniversities] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", city: "", state: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    loadUniversities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  async function loadUniversities(page = currentPage - 1, query = searchTerm) {
    try {
      setLoading(true);
      const data = await fetchUniversities(page, ROWS_PER_PAGE, query);
      setUniversities(data.content);
      setTotalPages(data.totalPages); // You’ll need this state
      setCurrentPage(data.page + 1); // Backend is 0-indexed, FE is 1-indexed
    } catch (err) {
      console.error("Failed to fetch universities", err);
      alert("Failed to load universities.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }

    try {
      if (isEditing) {
        const updated = await updateUniversity(form.id, form);
        setUniversities((prev) =>
          prev.map((uni) => (uni.id === form.id ? updated : uni)),
        );
        setIsEditing(false);
      } else {
        const created = await createUniversity(form);
        setUniversities((prev) => [...prev, created]);
      }
      setForm({ id: null, name: "", city: "", state: "" });
      //setCurrentPage(1); // Reset to first page on change
      setCurrentPage(1);
      loadUniversities(0, searchTerm); // Refresh list from server
    } catch (err) {
      console.error("Failed to save university", err);
      alert(err.response?.data?.message || "Error saving university.");
    }
  }

  function handleEdit(uni) {
    setForm(uni);
    setIsEditing(true);
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this university?"))
      return;

    try {
      await deleteUniversity(id);
      //setUniversities(prev => prev.filter(uni => uni.id !== id));
      loadUniversities(currentPage - 1, searchTerm);
      // If deletion reduces current page results, move back a page
      //const maxPage = Math.ceil(totalAfterDelete / ROWS_PER_PAGE);
      //if (currentPage > maxPage) setCurrentPage(maxPage);
    } catch (err) {
      console.error("Failed to delete university", err);
      alert("Error deleting university.");
    }
  }

  function handleCancel() {
    setForm({ id: null, name: "", city: "", state: "" });
    setIsEditing(false);
  }

  // Handle search input
  /*function handleSearchChange(e) {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }*/

  /*function handleSearchChange(e) {
    const value = e.target.value;
    setSearchTerm(value);
    loadUniversities(0, value); // Load with search
  }*/

  function handleSearchChange(e) {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    loadUniversities(0, value); // First page for new search
  }

  // Filtered universities by search term (case-insensitive)
  /*const filteredUniversities = universities.filter(uni =>
    uni.name.toLowerCase().includes(searchTerm.toLowerCase())
  );*/

  // Pagination calculations
  //const totalPages = Math.ceil(filteredUniversities.length / ROWS_PER_PAGE);
  //const currentUniversities = filteredUniversities.slice(startIndex, startIndex + ROWS_PER_PAGE);
  const currentUniversities = universities;

  // Pagination controls
  /*function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  }*/

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    loadUniversities(page - 1, searchTerm); // 0-indexed
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-3">Universities</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 max-w-md bg-gray-50 p-4 rounded shadow"
      >
        <div className="mb-3">
          <label className="block font-medium mb-1" htmlFor="name">
            University Name <span className="text-red-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
            required
          />
        </div>
        <div className="mb-3">
          <label className="block font-medium mb-1" htmlFor="city">
            City
          </label>
          <input
            id="city"
            name="city"
            type="text"
            value={form.city}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        </div>
        <div className="mb-3">
          <label className="block font-medium mb-1" htmlFor="state">
            State
          </label>
          <input
            id="state"
            name="state"
            type="text"
            value={form.state}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        </div>

        <div className="flex space-x-2">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            {isEditing ? "Update" : "Add"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Search box */}
      <div className="mb-3 max-w-md">
        <input
          type="text"
          placeholder="Search universities by name..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
        />
      </div>

      {/* Table */}
      <table className="min-w-full border border-gray-300 rounded shadow">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2 text-left">ID</th>
            <th className="border px-4 py-2 text-left">Name</th>
            <th className="border px-4 py-2 text-left">City</th>
            <th className="border px-4 py-2 text-left">State</th>
            <th className="border px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="text-center p-4 text-gray-500">
                Loading...
              </td>
            </tr>
          ) : currentUniversities.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center p-4 text-gray-500">
                No universities found.
              </td>
            </tr>
          ) : (
            currentUniversities.map((uni) => (
              <tr key={uni.id} className="hover:bg-gray-50">
                <td className="border px-4 py-2">{uni.id}</td>
                <td className="border px-4 py-2">{uni.name}</td>
                <td className="border px-4 py-2">{uni.city}</td>
                <td className="border px-4 py-2">{uni.state}</td>
                <td className="border px-4 py-2 text-center space-x-2">
                  <button
                    onClick={() => handleEdit(uni)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(uni.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center space-x-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {[...Array(totalPages).keys()].map((num) => {
            const page = num + 1;
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 2 && page <= currentPage + 2)
            ) {
              return (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-1 border rounded ${
                    page === currentPage ? "bg-blue-600 text-white" : ""
                  }`}
                >
                  {page}
                </button>
              );
            }
            if (page === currentPage - 3 || page === currentPage + 3) {
              return (
                <span key={page} className="px-2 py-1">
                  ...
                </span>
              );
            }
            return null;
          })}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default UniversityTab;
